/**
 * Execution block: sets or clears the active state on a SceneObject's
 * Properties behavior — SCOPED to the target's state group.
 *
 * `trace/setState(target, s)` sets the slot of `target` AND of every
 * participating descendant within the target's scope (pruned at nested
 * `stateGroup` boundaries), resolved against the live scene at execution time.
 * A named state applies the state's value/transform overrides with transitions;
 * an empty/undefined state reverts each member to base. See
 * docs/canopy/STATE_SCOPE_SPEC.md and core/canopy/stateScope.ts.
 *
 * If the target itself has no Properties behavior, the block logs a warning and
 * still processes the target's descendants (R6).
 */

import { FlowGraphExecutionBlockWithOutSignal } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlockWithOutSignal";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeString } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { resolveTarget } from './traceContext';
import { Properties } from '../../properties';
import { collectScopedStateTargets } from '../../../core/canopy/stateScope';
import { Logger } from '../../../utils/Logger';

export class TraceSetStateBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this.registerDataInput("state", RichTypeString, '');
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const target = resolveTarget(context, this._targetPath);
        if (target) {
            const raw = this.getDataInput("state")!.getValue(context) as string;
            const state = raw === '' ? undefined : raw;
            // Scoped write (R1–R3, R5): target first, then every participating
            // descendant, pruned at nested stateGroup boundaries. collect returns
            // the target as element 0 unconditionally, so a propertyless target
            // still lets its descendants through — we just warn on its own skip.
            const scope = collectScopedStateTargets(target, state);
            for (const obj of scope) {
                const props = obj.getBehavior(Properties);
                if (props) {
                    // R6/R7 live in Properties/PropertyResolver: undeclared named
                    // state is a silent no-op; same-value write is a strict no-op.
                    props.setState(state);
                } else if (obj === target) {
                    Logger.warn(`[TraceSetStateBlock] Target "${target.name}" has no Properties behavior`);
                }
            }
        }
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TraceSetStateBlock";
    }
}
